import React, { useEffect, useRef, useState } from 'react';
import { useModel } from '../context/ModelContext';
import { Scenario, hasTariffShock, hasKappaShock } from '../types/model';
import { showToast } from '../utils/toast';
import { Bookmark, ChevronDown, Download, Pencil, Trash2, Check } from 'lucide-react';
import { datasetLabel, modelLabel } from '../utils/modelTargets';

interface ScenarioManagerProps {
  onEdit: (scenario: Scenario) => void;
}

const ScenarioManager: React.FC<ScenarioManagerProps> = ({ onEdit }) => {
  const { 
    scenarios, storageWarning,
    selectedScenarioIds, 
    updateScenarioSelection, 
    deleteScenario, 
    clearAllScenarios,
    // Per-scenario caching
    saveScenarioToCache,
    deleteScenarioFromCache,
    loadMultipleCachedScenarios,
    clearAllCache,
    cachedScenariosList,
    refreshCachedScenariosList,
  } = useModel();
  
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [isLoadingCached, setIsLoadingCached] = useState(false);
  const [selectedCachedIds, setSelectedCachedIds] = useState<Set<string>>(new Set());
  const [showCachePanel, setShowCachePanel] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState<{ id?: string } | null>(null);
  const deleteDialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (deleteRequest) deleteDialog.current?.showModal(); }, [deleteRequest]);


  const handleToggleSelection = (id: string) => {
    if (selectedScenarioIds.includes(id)) {
      updateScenarioSelection(selectedScenarioIds.filter(sid => sid !== id));
    } else {
      updateScenarioSelection([...selectedScenarioIds, id]);
    }
  };

  const handleSelectAll = () => {
    const solvedIds = scenarios.filter(s => s.status === 'solved').map(s => s.id);
    updateScenarioSelection(solvedIds);
  };

  const handleClearSelection = () => {
    updateScenarioSelection([]);
  };

  const solvedScenarios = scenarios.filter(s => s.status === 'solved');
  const selectedCount = selectedScenarioIds.length;

  // Per-scenario cache handlers
  const handleSaveToCache = async (scenarioId: string) => {
    setSavingIds(prev => new Set(prev).add(scenarioId));
    try {
      await saveScenarioToCache(scenarioId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      showToast(`Error: ${message}`, 'error');
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(scenarioId);
        return next;
      });
    }
  };

  const handleDeleteFromCache = async (scenarioId: string) => {
    try {
      await deleteScenarioFromCache(scenarioId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete';
      showToast(`Error: ${message}`, 'error');
    }
  };

  const handleClearAllCache = async () => {
    try {
      await clearAllCache();
      setSelectedCachedIds(new Set());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clear cache';
      showToast(`Error: ${message}`, 'error');
    }
  };

  const handleLoadSelected = async () => {
    if (selectedCachedIds.size === 0) return;
    setIsLoadingCached(true);
    try {
      await loadMultipleCachedScenarios(Array.from(selectedCachedIds));
      setSelectedCachedIds(new Set());
      setShowCachePanel(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load';
      showToast(`Error: ${message}`, 'error');
    } finally {
      setIsLoadingCached(false);
    }
  };

  const toggleCachedSelection = (id: string) => {
    setSelectedCachedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Check if a cached scenario is already loaded
  const isAlreadyLoaded = (cachedId: string) => scenarios.some(s => s.id === cachedId);

  // Check if a scenario is cached
  const isCached = (scenarioId: string) => cachedScenariosList.some(c => c.id === scenarioId);


  return <section className="scenario-manager" aria-label="Scenarios">
    <div className="scenario-heading">
      <h2>Runs <span className="scenario-count">{scenarios.length}</span></h2>
      <div className="scenario-actions">
        <button className="simulation-button simulation-button-secondary" aria-expanded={showCachePanel}
          aria-controls="saved-scenarios" onClick={() => {
            setShowCachePanel(!showCachePanel);
            if (!showCachePanel) void refreshCachedScenariosList().catch(() => showToast('Could not load saved scenarios', 'error'));
          }}><Bookmark size={15} aria-hidden="true" />Saved{cachedScenariosList.length > 0 && ` (${cachedScenariosList.length})`}<ChevronDown size={14} aria-hidden="true" /></button>
      </div>
    </div>

    {storageWarning && <p role="alert" className="control-help">Browser storage cannot retain all results. Use each run’s Save button before reloading; reopen them from Saved.</p>}
    {showCachePanel && <section id="saved-scenarios" className="saved-scenarios" aria-label="Saved scenarios">
      <div className="scenario-selection-bar">
        <strong>Saved scenarios</strong>
        <div className="scenario-actions">
          {selectedCachedIds.size > 0 && <button onClick={handleLoadSelected} disabled={isLoadingCached}
            className="simulation-button simulation-button-primary"><Download size={15} aria-hidden="true" />{isLoadingCached ? 'Loading…' : `Load selected (${selectedCachedIds.size})`}</button>}
          {cachedScenariosList.length > 0 && <button onClick={() => setDeleteRequest({})} className="simulation-text-button danger">Delete all saved</button>}
        </div>
      </div>
      {cachedScenariosList.length === 0 ? <p className="simulation-empty">No saved scenarios.</p> : <div className="saved-scenario-list">
        {cachedScenariosList.map(cached => {
          const loaded = isAlreadyLoaded(cached.id);
          return <div key={cached.id} className="saved-scenario-row">
            <label className="scenario-row-main">
              <input type="checkbox" checked={loaded || selectedCachedIds.has(cached.id)} disabled={loaded}
                onChange={() => toggleCachedSelection(cached.id)} />
              <span><strong>{cached.name}</strong><span className="scenario-meta">{modelLabel(cached.framework)} · {datasetLabel(cached.dataset)}</span></span>
            </label>
            {loaded ? <span className="scenario-status">Loaded</span> : <button onClick={() => setDeleteRequest({id: cached.id})}
              className="simulation-icon-button danger" aria-label={`Delete saved ${cached.name}`} title="Delete saved scenario"><Trash2 size={15} aria-hidden="true" /></button>}
          </div>;
        })}
      </div>}
    </section>}

    {scenarios.length === 0 ? null : <>
      <div className="scenario-selection-bar">
        <span>{selectedCount} selected</span>
        <div className="scenario-actions">
          {solvedScenarios.length > 0 && <>
            <button onClick={handleSelectAll} className="simulation-text-button">Select all</button>
            <button onClick={handleClearSelection} disabled={selectedCount === 0} className="simulation-text-button">Deselect</button>
          </>}
          <button onClick={clearAllScenarios} className="simulation-text-button danger">Clear all</button>
        </div>
      </div>
      <div className="scenario-list">
        {scenarios.map(scenario => {
          const selected = selectedScenarioIds.includes(scenario.id);
          const tariffCount = Object.keys(scenario.tariffConfig.tariff_data).length;
          const kappaCount = Object.keys(scenario.kappaConfig?.kappa_S ?? {}).length + Object.keys(scenario.kappaConfig?.kappa_D ?? {}).length;
          const shocks = [hasTariffShock(scenario.tariffConfig) ? `${tariffCount} tariff changes` : '', hasKappaShock(scenario.kappaConfig) ? `${kappaCount} κ edits` : ''].filter(Boolean).join(' · ');
          return <div key={scenario.id} className={`scenario-row${selected ? ' is-selected' : ''}`}>
            <label className="scenario-row-main">
              <input type="checkbox" checked={selected} disabled={scenario.status !== 'solved'}
                aria-label={`Show ${modelLabel(scenario.framework)} · ${scenario.name}`} onChange={() => handleToggleSelection(scenario.id)} />
              <span className="scenario-identity"><strong>{scenario.name}</strong>
                <span className="scenario-meta"><span className="model-tag">{modelLabel(scenario.framework)}</span>{shocks}</span>
                <span className="scenario-dataset">{datasetLabel(scenario.dataset)}</span>
              </span>
            </label>
            <span className={`scenario-status status-${scenario.status}`} role={scenario.status === 'error' ? 'alert' : undefined}>
              {scenario.status === 'solved' ? 'Ready' : scenario.status === 'solving' ? 'Solving…' : scenario.status === 'error' ? scenario.error || 'Failed' : 'Queued'}
            </span>
            <div className="scenario-actions">
              <button onClick={() => onEdit(scenario)} className="simulation-icon-button" disabled={scenario.status === 'solving'} aria-label={`Edit ${scenario.name}`} title="Revise as a new run"><Pencil size={16} aria-hidden="true" /></button>
              {scenario.status === 'solved' && !hasKappaShock(scenario.kappaConfig) && <button onClick={() => handleSaveToCache(scenario.id)}
                className="simulation-icon-button" disabled={savingIds.has(scenario.id)} aria-label={`Save ${scenario.name}`} title={isCached(scenario.id) ? 'Update saved scenario' : 'Save scenario'}>
                {savingIds.has(scenario.id) ? '…' : isCached(scenario.id) ? <Check size={16} aria-hidden="true" /> : <Bookmark size={16} aria-hidden="true" />}
              </button>}
              <button onClick={() => deleteScenario(scenario.id)} className="simulation-icon-button danger" disabled={scenario.status === 'solving'} aria-label={`Delete ${scenario.name}`} title="Delete scenario"><Trash2 size={16} aria-hidden="true" /></button>
            </div>
          </div>;
        })}
      </div>
    </>}
    {deleteRequest && <dialog ref={deleteDialog} className="delete-confirmation" aria-labelledby="delete-saved-title"
      onCancel={event => { event.preventDefault(); setDeleteRequest(null); }}>
      <h2 id="delete-saved-title">{deleteRequest.id ? 'Delete saved scenario?' : 'Delete all saved scenarios?'}</h2>
      <p>{deleteRequest.id ? cachedScenariosList.find(s => s.id === deleteRequest.id)?.name : `${cachedScenariosList.length} saved scenarios will be removed.`} This cannot be undone.</p>
      <div className="scenario-actions">
        <button autoFocus className="simulation-button simulation-button-secondary" onClick={() => setDeleteRequest(null)}>Cancel</button>
        <button className="simulation-button simulation-button-primary" onClick={() => {
          const request = deleteRequest; setDeleteRequest(null);
          if (request.id) void handleDeleteFromCache(request.id); else void handleClearAllCache();
        }}>Delete permanently</button>
      </div>
    </dialog>}
  </section>;
};

export default ScenarioManager;
